import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { ChevronRight, ChevronDown, Search, Menu, X, MapPin, Phone, Mail, Leaf, Briefcase, Newspaper, User, ShoppingCart, Globe } from "lucide-react";
import { LOGO, LOGO_DARK, SITE_NAME } from "../lib/api";
import { LanguageSwitcher, swapLang } from "./LanguageSwitcher";
import { LANGS } from "../i18n";
import { lp } from "../lib/urls";
import { usePartTypeMeta, usePartTypeCounts } from "../lib/usePartTypes";
import { splitTerm } from "../lib/localizeCatalog";
import { PART_TYPE_ORDER } from "../lib/partTypes";
import { TYPE_IMAGES } from "../lib/typeImages";
import { FOOTER_OFFICES } from "../lib/regions";
import { useQuoteCart } from "../context/QuoteCartContext";

const HeaderQuoteCart = ({ mobile = false }) => {
  const { t } = useTranslation();
  const { items, openCart } = useQuoteCart();
  return (
    <button
      type="button"
      data-testid={mobile ? "mobile-quote-cart-button" : "header-quote-cart-button"}
      onClick={openCart}
      aria-label={t("qc.fab")}
      title={t("qc.fab")}
      className={`relative flex items-center px-2 text-gray-950 hover:text-yellow-600 transition-colors ${mobile ? "" : "-mt-2 py-2"}`}
    >
      <ShoppingCart size={19} strokeWidth={2.2} />
      {items.length > 0 && (
        <span
          data-testid={mobile ? "mobile-quote-cart-count" : "header-quote-cart-count"}
          className="absolute -top-1 right-0 bg-yellow-500 text-black font-mono-tech font-bold text-[10px] leading-none px-1 py-0.5"
        >
          {items.length}
        </span>
      )}
    </button>
  );
};
import { useFinalDrives } from "./FinalDrivesInterstitial";

const FOOTER_TYPES = ["track-roller", "carrier-roller", "idler", "sprocket", "track-chain", "track-shoe", "track-group", "rubber-track", "rubber-pad", "track-bolt-nut"];

export const Header = () => {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [mobProducts, setMobProducts] = useState(false);
  const [mobLangs, setMobLangs] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { i18n } = useTranslation();
  const curLang = (i18n.language || "en").slice(0, 2);
  const curLangLabel = (LANGS.find((l) => l.code === curLang) || LANGS[0]).label;

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);
  const getTypeMeta = usePartTypeMeta();
  const typeCounts = usePartTypeCounts();
  const { openFinalDrives, openGroupSite } = useFinalDrives();

  const submit = (e) => {
    e.preventDefault();
    if (q.trim()) navigate(lp(`/search?q=${encodeURIComponent(q.trim())}`));
  };

  return (
    <header className="sticky top-0 z-50 bg-white">
      <div className="w-full hidden md:flex items-center justify-end gap-7 px-4 sm:px-10 lg:px-14 pt-3.5 pb-3 border-b border-border">
        <Link to={lp("/sustainability")} data-testid="nav-sustainability-link" className="font-euro font-bold text-[12px] tracking-[0.1em] uppercase text-gray-400 hover:text-gray-950 transition-colors">{t("sust.navLabel")}</Link>
        <Link to={lp("/careers")} data-testid="nav-careers-link" className="font-euro font-bold text-[12px] tracking-[0.1em] uppercase text-gray-400 hover:text-gray-950 transition-colors">{t("careers.navLabel")}</Link>
        <Link to={lp("/newsroom")} data-testid="nav-newsroom-link" className="font-euro font-bold text-[12px] tracking-[0.1em] uppercase text-gray-400 hover:text-gray-950 transition-colors">{t("news.navLabel")}</Link>
        <span className="w-px h-3.5 bg-border" />
        <Link to={lp("/dealer-login")} data-testid="nav-dealer-login-link" className="font-euro font-bold text-[12px] tracking-[0.1em] uppercase text-gray-400 hover:text-gray-950 transition-colors">{t("dportal.navLogin")}</Link>
        <span className="w-px h-3.5 bg-border" />
        <button type="button" onClick={openGroupSite} data-testid="nav-global-link" className="font-euro font-bold text-[12px] tracking-[0.1em] uppercase text-gray-950 hover:[-webkit-text-stroke:0.3px_currentColor] transition-colors">
          VANROCK Group ↗
        </button>
      </div>
      <div className="w-full px-4 sm:px-10 lg:px-14">
        <div className="flex items-center py-[26px] md:py-0 md:mt-5">
          <Link to={lp("/")} data-testid="header-logo-link" className="flex items-baseline gap-1 w-fit shrink-0">
            <img src="/vanrock-logo-word.png" alt={`${SITE_NAME} logo`} className="h-7 sm:h-8 w-auto" />
            <img src="/vanrock-track-tag-dark.png" alt=".TRACK" className="h-3 sm:h-3.5 w-auto" />
          </Link>
          <button data-testid="mobile-menu-toggle" className="md:hidden px-2 -mr-2 text-gray-950 order-2" onClick={() => setOpen(!open)} aria-label="Menu">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
          <span className="md:hidden ml-auto order-1">
            <HeaderQuoteCart mobile />
          </span>
        </div>
        <div className="hidden md:flex items-start mt-4">
        <nav className="flex items-stretch gap-8 font-euro text-[14px] tracking-[0.04em] uppercase">
          <div className="relative group flex items-stretch">
            <button type="button" data-testid="nav-company-link" className="flex items-center gap-1.5 text-gray-800 hover:text-gray-950 pb-5 transition-colors uppercase">
              <span className="group-hover:[-webkit-text-stroke:0.3px_currentColor]">{t("nav.company")}</span>
              <ChevronDown size={12} className="transition-transform group-hover:rotate-180" />
            </button>
            <div
              className="absolute left-0 top-full w-64 bg-white border border-border border-t-2 border-t-yellow-500 shadow-[0_18px_40px_rgba(0,0,0,0.12)] invisible opacity-0 translate-y-1 transition-[opacity,transform,visibility] duration-150 group-hover:visible group-hover:opacity-100 group-hover:translate-y-0 z-50"
              data-testid="nav-company-menu"
            >
              {[
                { to: "/company", testid: "nav-company-sub-link", label: t("nav.company") },
                { to: "/mission-vision-values", testid: "nav-mvv-link", label: t("mvv.navLabel") },
                { to: "/why-vanrock", testid: "nav-why-link", label: t("footer2.why") },
                { to: "/our-promises", testid: "nav-promises-link", label: t("promises.navLabel") },
              ].map(({ to, testid, label }, i, all) => (
                <Link
                  key={to}
                  to={lp(to)}
                  data-testid={testid}
                  className={`group/item flex items-center gap-4 px-5 py-3.5 transition-colors duration-150 hover:bg-yellow-500 ${i < all.length - 1 ? "border-b border-border" : ""}`}
                >
                  <span className="font-mono-tech text-[9px] text-gray-600 transition-colors group-hover/item:text-black/50">0{i + 1}</span>
                  <span className="font-euro text-[13px] tracking-[0.04em] uppercase text-gray-950 transition-colors group-hover/item:text-black">{label}</span>
                  <span className="ml-auto font-display font-black text-sm text-gray-300 transition-all duration-150 group-hover/item:text-black group-hover/item:translate-x-0.5">→</span>
                </Link>
              ))}
            </div>
          </div>
          <Link to={lp("/applications")} data-testid="nav-applications-link" className="group/nav flex items-center text-gray-800 hover:text-gray-950 pb-5 transition-colors uppercase">
            <span className="group-hover/nav:[-webkit-text-stroke:0.3px_currentColor]">{t("apps.navLabel")}</span>
          </Link>
          <div className="group flex items-stretch">
            <button type="button" data-testid="nav-products-link" className="flex items-center gap-1.5 text-gray-800 hover:text-gray-950 pb-5 transition-colors uppercase">
              <span className="group-hover:[-webkit-text-stroke:0.3px_currentColor]">{t("nav.products")}</span>
              <ChevronDown size={12} className="transition-transform group-hover:rotate-180" />
            </button>
            <div className="absolute left-0 right-0 top-full invisible opacity-0 transition-[opacity,visibility] duration-100 delay-150 group-hover:visible group-hover:opacity-100 group-hover:delay-0 before:content-[''] before:absolute before:inset-x-0 before:-top-3 before:h-3 bg-white border-b-2 border-yellow-500 shadow-[0_28px_56px_rgba(0,0,0,0.18)] z-50" data-testid="nav-products-menu">
              <div className="flex">
                <div className="hidden lg:flex w-72 shrink-0 border-r border-border p-8 flex-col bg-gray-50/60">
                  <p className="font-mono-tech text-[9px] tracking-[0.3em] uppercase text-gray-400">Undercarriage</p>
                  <p className="mt-3 font-display font-black uppercase tracking-tighter text-[26px] leading-[0.95] text-gray-950">{t("nav.products")}</p>
                  <p className="mt-4 text-xs text-gray-500 leading-relaxed normal-case tracking-normal font-normal [font-family:Manrope,sans-serif]">{t("nav.productsLine")}</p>
                </div>
                <div className="flex-1 grid grid-cols-5">
                  {PART_TYPE_ORDER.map((slug, i) => {
                    const term = splitTerm(getTypeMeta(slug).name);
                    const count = typeCounts && typeCounts[slug];
                    return (
                      <Link
                        key={slug}
                        to={lp(`/parts/${slug}`)}
                        data-testid={`nav-product-${slug}`}
                        className={`group/item flex flex-col px-6 pt-5 pb-5 border-border ${(i + 1) % 5 !== 0 ? "border-r" : ""} ${i < 5 ? "border-b" : ""}`}
                      >
                        <span className="h-32 flex items-center justify-center">
                          <img
                            src={TYPE_IMAGES[slug]}
                            alt={term.local}
                            className="max-h-full max-w-[210px] object-contain transition-transform duration-200 group-hover/item:scale-[1.06]"
                            loading="lazy"
                          />
                        </span>
                        <span className="mt-3.5 self-start font-display font-black uppercase tracking-tight text-[12px] leading-tight text-gray-950 px-1.5 py-0.5 -mx-1.5 group-hover/item:bg-yellow-500 transition-colors duration-150">
                          {term.local}
                        </span>
                        <span className={`mt-1 font-mono-tech text-[9px] uppercase tracking-[0.12em] leading-tight ${term.en ? "text-gray-400" : "text-transparent select-none"}`}>{term.en || "·"}</span>
                        {count != null && (
                          <span className="mt-2 font-mono-tech text-[9px] uppercase tracking-[0.15em] text-gray-500">
                            {count.toLocaleString()} {t("hub.parts")}
                          </span>
                        )}
                      </Link>
                    );
                  })}
                  <button
                    type="button"
                    onClick={openFinalDrives}
                    data-testid="nav-product-final-drive"
                    className="group/item flex flex-col px-6 pt-5 pb-5 border-border border-r text-left"
                  >
                    <span className="h-32 flex items-center justify-center">
                      <img
                        src="/components/final-drive.webp"
                        alt={t("fd.navLabel")}
                        className="max-h-full max-w-[210px] object-contain transition-transform duration-200 group-hover/item:scale-[1.06]"
                        loading="lazy"
                      />
                    </span>
                    <span className="mt-3.5 self-start font-display font-black uppercase tracking-tight text-[12px] leading-tight text-gray-950 px-1.5 py-0.5 -mx-1.5 group-hover/item:bg-yellow-500 transition-colors duration-150">
                      {t("fd.navLabel", "Final Drives")}
                    </span>
                    <span className="mt-2 flex items-center gap-1 font-mono-tech text-[9px] uppercase tracking-[0.15em] text-yellow-600">
                      {t("fd.online", "Online store")} ↗
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>
          <Link to={lp("/brands")} data-testid="nav-brands-link" className="group/nav flex items-center text-gray-800 hover:text-gray-950 pb-5 transition-colors">
            <span className="group-hover/nav:[-webkit-text-stroke:0.3px_currentColor]">{t("nav.brands")}</span>
          </Link>
          <div className="relative group flex items-stretch">
            <button data-testid="nav-dealer-network-btn" className="flex items-center gap-1.5 text-gray-800 hover:text-gray-950 pb-5 transition-colors uppercase">
              <span className="group-hover:[-webkit-text-stroke:0.3px_currentColor]">{t("footer3.dealerNetwork")}</span>
              <ChevronDown size={12} className="transition-transform group-hover:rotate-180" />
            </button>
            <div
              className="absolute left-0 top-full w-64 bg-white border border-border border-t-2 border-t-yellow-500 shadow-[0_18px_40px_rgba(0,0,0,0.12)] invisible opacity-0 translate-y-1 transition-[opacity,transform,visibility] duration-150 group-hover:visible group-hover:opacity-100 group-hover:translate-y-0 z-50"
              data-testid="nav-dealer-menu"
            >
              {[
                { to: "/dealers", testid: "nav-find-dealer-link", label: t("footer2.findDealer") },
                { to: "/become-a-dealer", testid: "nav-become-dealer-link", label: t("footer.becomeDealer").replace("→", "").trim() },
              ].map(({ to, testid, label }, i, all) => (
                <Link
                  key={to}
                  to={lp(to)}
                  data-testid={testid}
                  className={`group/item flex items-center gap-4 px-5 py-3.5 transition-colors duration-150 hover:bg-yellow-500 ${i < all.length - 1 ? "border-b border-border" : ""}`}
                >
                  <span className="font-mono-tech text-[9px] text-gray-600 transition-colors group-hover/item:text-black/50">0{i + 1}</span>
                  <span className="font-euro text-[13px] tracking-[0.04em] uppercase text-gray-950 transition-colors group-hover/item:text-black">{label}</span>
                  <span className="ml-auto font-display font-black text-sm text-gray-300 transition-all duration-150 group-hover/item:text-black group-hover/item:translate-x-0.5">→</span>
                </Link>
              ))}
            </div>
          </div>
          <Link to={lp("/contact")} data-testid="nav-contact-link" className="group/nav flex items-center text-gray-800 hover:text-gray-950 pb-5 transition-colors">
            <span className="group-hover/nav:[-webkit-text-stroke:0.3px_currentColor]">{t("contact2.navLabel")}</span>
          </Link>
        </nav>
        <form onSubmit={submit} className="ml-auto mr-4 -mt-2 hidden lg:flex items-center border border-gray-300 focus-within:border-gray-500 transition-colors">
          <input
            data-testid="header-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("nav.searchPlaceholder")}
            className="bg-transparent px-3.5 py-2 text-xs font-mono-tech w-44 xl:w-64 focus:w-56 xl:focus:w-80 transition-[width] duration-300 outline-none placeholder:text-gray-400 text-gray-950"
          />
          <button data-testid="header-search-submit" type="submit" aria-label="Search" className="px-4 self-stretch bg-yellow-500 text-black hover:bg-[#0A0A0A] hover:text-yellow-500 transition-colors">
            <Search size={15} strokeWidth={2.5} />
          </button>
        </form>
        <Link to={lp("/request-quote")} data-testid="nav-quote-link" className="md:ml-auto lg:ml-0 -mt-2 flex items-center bg-yellow-500 text-black px-5 py-2 font-euro font-bold text-[12px] uppercase tracking-[0.08em] hover:bg-[#0A0A0A] hover:text-yellow-500 transition-colors">
          {t("nav.quote", "Get Quote")}
        </Link>
        <HeaderQuoteCart />
        <div className="flex items-center -mt-2">
          <LanguageSwitcher />
        </div>
        </div>
      </div>
      {open && createPortal(
        <div className="md:hidden fixed inset-x-0 top-[85px] bottom-0 z-[60] bg-white overflow-y-auto overscroll-contain" data-testid="mobile-menu-overlay">
          <nav className="px-4 pb-10">
            <Link to={lp("/company")} onClick={() => setOpen(false)} data-testid="mobile-nav-company" className="flex items-center justify-between py-4 border-b border-border font-euro font-bold text-[15px] uppercase tracking-[0.06em] text-gray-950">
              {t("nav.company")}
              <ChevronRight size={16} className="text-gray-400" />
            </Link>
            <Link to={lp("/applications")} onClick={() => setOpen(false)} data-testid="mobile-nav-applications" className="flex items-center justify-between py-4 border-b border-border font-euro font-bold text-[15px] uppercase tracking-[0.06em] text-gray-950">
              {t("apps.navLabel")}
              <ChevronRight size={16} className="text-gray-400" />
            </Link>
            <button type="button" data-testid="mobile-nav-products-toggle" onClick={() => setMobProducts(!mobProducts)} className="w-full flex items-center justify-between py-4 border-b border-border font-euro font-bold text-[15px] uppercase tracking-[0.06em] text-gray-950">
              {t("nav.products")}
              <ChevronDown size={16} className={`text-gray-400 transition-transform ${mobProducts ? "rotate-180" : ""}`} />
            </button>
            {mobProducts && (
              <div className="border-b border-border py-1">
                {PART_TYPE_ORDER.map((slug) => (
                  <Link key={slug} to={lp(`/parts/${slug}`)} onClick={() => setOpen(false)} data-testid={`mobile-nav-product-${slug}`} className="block pl-4 pr-2 py-2.5 font-mono-tech text-xs uppercase tracking-[0.15em] text-gray-600">
                    {splitTerm(getTypeMeta(slug).name).local}
                  </Link>
                ))}
                <button
                  type="button"
                  onClick={() => { setOpen(false); openFinalDrives(); }}
                  data-testid="mobile-nav-product-final-drive"
                  className="w-full text-left flex items-center gap-2 pl-4 pr-2 py-2.5 font-mono-tech text-xs uppercase tracking-[0.15em] text-gray-900"
                >
                  {t("fd.navLabel", "Final Drives")}
                  <span className="font-mono-tech text-[9px] uppercase tracking-[0.15em] bg-yellow-500 text-black px-1.5 py-0.5">{t("fd.store", "Store")}</span>
                </button>
              </div>
            )}
            <Link to={lp("/brands")} onClick={() => setOpen(false)} data-testid="mobile-nav-brands" className="flex items-center justify-between py-4 border-b border-border font-euro font-bold text-[15px] uppercase tracking-[0.06em] text-gray-950">
              {t("nav.brands")}
              <ChevronRight size={16} className="text-gray-400" />
            </Link>
            <div className="grid grid-cols-2" data-testid="mobile-nav-secondary-grid">
              {[
                { to: "/mission-vision-values", label: t("mvv.navLabel") },
                { to: "/sustainability", label: t("sust.navLabel") },
                { to: "/why-vanrock", label: t("footer2.why") },
                { to: "/our-promises", label: t("promises.navLabel") },
                { to: "/careers", label: t("careers.navLabel") },
                { to: "/newsroom", label: t("news.navLabel") },
                { to: "/contact", label: t("contact2.navLabel") },
                { to: "/dealers", label: t("footer2.findDealer") },
                { to: "/become-a-dealer", label: t("footer.becomeDealer") },
                { to: "/dealer-login", label: t("dportal.navLogin"), testid: "mobile-nav-dealer-login" },
              ].map(({ to, label, testid }, i) => (
                <Link
                  key={to}
                  to={lp(to)}
                  onClick={() => setOpen(false)}
                  data-testid={testid || `mobile-nav-sec-${to.replace(/\//g, "")}`}
                  className={`flex items-center py-3.5 border-b border-border font-mono-tech text-[11px] uppercase tracking-[0.12em] text-gray-700 ${i % 2 === 0 ? "pr-3 border-r" : "pl-4"}`}
                >
                  {label}
                </Link>
              ))}
            </div>
            <Link to={lp("/request-quote")} onClick={() => setOpen(false)} data-testid="mobile-nav-quote-link" className="mt-5 flex items-center justify-center gap-2 w-full bg-yellow-500 text-black py-4 font-euro font-bold text-[14px] uppercase tracking-[0.08em] rounded-md">
              {t("nav.quote", "Get Quote")}
              <span aria-hidden="true">↗</span>
            </Link>
            <button
              type="button"
              data-testid="mobile-language-toggle"
              onClick={() => setMobLangs(!mobLangs)}
              className="mt-3 w-full border border-border rounded-md flex items-center justify-center gap-2.5 py-3.5 font-mono-tech text-[12px] font-bold uppercase tracking-[0.15em] text-gray-900"
            >
              <Globe size={15} strokeWidth={2} className="text-gray-500" />
              {curLangLabel} / {curLang.toUpperCase()}
              <ChevronDown size={13} className={`text-gray-400 transition-transform ${mobLangs ? "rotate-180" : ""}`} />
            </button>
            {mobLangs && (
              <div className="mt-2 grid grid-cols-3 gap-2" data-testid="mobile-language-switcher">
                {LANGS.map((l) => (
                  <button
                    key={l.code}
                    data-testid={`mobile-language-option-${l.code}`}
                    onClick={() => swapLang(l.code)}
                    className={`py-2.5 rounded-md font-mono-tech text-xs uppercase tracking-[0.12em] border ${curLang === l.code ? "bg-yellow-500 text-black border-yellow-500 font-bold" : "text-gray-700 border-gray-300"}`}
                  >
                    {l.label}
                  </button>
                ))}
              </div>
            )}
            <button type="button" onClick={openGroupSite} data-testid="mobile-group-link" className="mt-5 w-full text-center font-mono-tech text-[11px] uppercase tracking-[0.2em] text-gray-500">
              VANROCK Group ↗
            </button>
          </nav>
        </div>,
        document.body
      )}
      <div aria-hidden="true" className="h-[5px] w-full bg-yellow-500" />
    </header>
  );
};

export const Breadcrumbs = ({ items }) => {
  const { t } = useTranslation();
  const label = (n) => (n === "Home" ? t("qa2.home") : n);
  return (
  <nav aria-label="Breadcrumb" data-testid="breadcrumbs" className="border-b border-border bg-[#f4f4f1]">
    <div className="px-4 sm:px-10 lg:px-14 py-3 text-[11px] font-mono-tech uppercase tracking-[0.15em] text-muted-foreground flex items-center flex-wrap gap-1">
      {items.map((it, i) => (
        <span key={i} className="flex items-center gap-1">
          {i > 0 && <ChevronRight size={12} aria-hidden="true" />}
          {it.path ? (
            <Link to={lp(it.path)} className="hover:text-gray-950 transition-colors">{label(it.name)}</Link>
          ) : (
            <span className="text-gray-950 font-bold">{label(it.name)}</span>
          )}
        </span>
      ))}
    </div>
  </nav>
  );
};

const OFFICE_FLAG = { europe: "de", usa: "us", australia: "au", india: "in" };
const FooterFlag = ({ cc, className = "" }) => (
  <img src={`https://flagcdn.com/w40/${cc}.png`} srcSet={`https://flagcdn.com/w80/${cc}.png 2x`} alt="" aria-hidden="true" loading="lazy" className={`h-[12px] w-auto border border-white/15 ${className}`} data-testid={`footer-flag-${cc}`} />
);

const OFFICE_META = {
  usa: { city: "Sheridan", country: "USA", tz: "America/Denver", cc: "US" },
  europe: { city: "Munich", country: "Germany", tz: "Europe/Berlin", cc: "DE" },
  australia: { city: "Dandenong", country: "Dandenong", tz: "Australia/Melbourne", cc: "AU" },
  india: { city: "Ludhiana", country: "Ludhiana", tz: "Asia/Kolkata", cc: "IN" },
};

const LANG_OFFICE = { de: "europe", fr: "europe", it: "europe", nl: "europe", ru: "europe", lt: "europe" };
const officeForLang = (lng) => LANG_OFFICE[(lng || "").split("-")[0]] || FOOTER_OFFICES[0].key;

export const flagEmoji = (cc) =>
  (cc || "").toUpperCase().replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt(0)));

export const Flag = ({ cc, className = "", testid }) => {
  const c = (cc || "").toLowerCase();
  return (
    <img
      src={`https://flagcdn.com/w40/${c}.png`}
      srcSet={`https://flagcdn.com/w80/${c}.png 2x`}
      alt=""
      aria-hidden="true"
      loading="lazy"
      data-testid={testid}
      className={`inline-block h-auto object-contain ${className}`}
    />
  );
};

export const Footer = () => {
  const { t, i18n } = useTranslation();
  const getMeta = usePartTypeMeta();
  const { openFinalDrives, openGroupSite } = useFinalDrives();
  const [officeKey, setOfficeKey] = useState(() => officeForLang(i18n.language));
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    setOfficeKey(officeForLang(i18n.language));
  }, [i18n.language]);
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(id);
  }, []);
  const office = FOOTER_OFFICES.find((o) => o.key === officeKey);
  const meta = OFFICE_META[officeKey];
  const localTime = new Intl.DateTimeFormat(i18n.language, {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: meta.tz,
  }).format(now);
  return (
  <footer
    className=""
    onClick={(e) => {
      const a = e.target.closest && e.target.closest("a");
      if (a && a.getAttribute("href") === window.location.pathname) {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    }}
  >
    <div className="bg-[#0A0A0A] text-gray-300">
    <div className="max-w-none mx-auto px-4 sm:px-10 lg:px-14">
      <div className="grid lg:grid-cols-[2fr_3fr]">
        <div className="flex flex-col lg:border-r border-white/10" data-testid="footer-offices">
          <div className="pr-0 sm:pr-8 py-12 sm:py-20 flex-1">
            <div className="flex flex-wrap items-center justify-between gap-x-8 gap-y-4">
              <h3 className="font-mono-tech text-[11px] uppercase tracking-[0.25em] text-yellow-500">{t("footer3.title")}</h3>
              <div className="grid grid-cols-2 auto-rows-fr gap-1.5 w-full sm:w-auto sm:flex sm:gap-0 sm:border sm:border-white/15" role="tablist" aria-label={t("footer3.title")}>
                {FOOTER_OFFICES.map((o) => (
                  <button
                    key={o.key}
                    type="button"
                    role="tab"
                    aria-selected={officeKey === o.key}
                    data-testid={`footer-office-tab-${o.key}`}
                    onClick={() => setOfficeKey(o.key)}
                    className={`px-3 sm:px-5 py-2.5 font-mono-tech text-[10px] uppercase tracking-[0.15em] sm:tracking-[0.2em] transition-colors border border-white/15 sm:border-0 sm:border-r sm:border-white/15 sm:last:border-r-0 ${
                      officeKey === o.key ? "bg-yellow-500 text-black font-bold border-yellow-500" : "text-gray-400 hover:text-white"
                    }`}
                  >
                    {t(`footer3.${o.key}`)}
                  </button>
                ))}
              </div>
            </div>
            <div key={officeKey} className="relative overflow-hidden pt-6 office-fade" data-testid="footer-office-panel">
              <div className="relative max-w-2xl">
                <p className="flex items-baseline gap-4 font-mono-tech text-[10px] uppercase tracking-[0.25em]">
                  <span className="inline-flex items-center gap-2 font-bold text-yellow-500"><FooterFlag cc={OFFICE_FLAG[officeKey]} /> {t(`footer3.${officeKey}`)} — {meta.country}</span>
                  <span className="text-gray-500" data-testid="footer-office-localtime">{localTime}</span>
                </p>
                <p className="mt-3 font-display font-black uppercase tracking-tighter text-base sm:text-lg leading-none text-white">
                  {office.company}
                </p>
                {office.subtitle && <p className="mt-2 text-xs text-gray-500">{office.subtitle}</p>}
                <p className="mt-4 flex items-start gap-2 text-sm text-gray-400 max-w-md">
                  <MapPin size={14} className="shrink-0 mt-0.5 text-gray-500" aria-hidden="true" />
                  {office.address}
                </p>
                <p className="mt-3 flex flex-wrap gap-x-6 gap-y-1.5">
                  {office.phone && (
                    <a href={`tel:${office.phone.replace(/\s/g, "")}`} data-testid={`footer-office-phone-${officeKey}`} className="inline-flex items-center gap-2 font-mono-tech text-sm text-gray-300 hover:text-yellow-500 transition-colors">
                      <Phone size={13} className="shrink-0 text-gray-500" aria-hidden="true" />
                      {office.phone}
                    </a>
                  )}
                  {office.email && (
                    <a href={`mailto:${office.email}`} data-testid={`footer-office-email-${officeKey}`} className="inline-flex items-center gap-2 font-mono-tech text-sm text-gray-300 break-all hover:text-yellow-500 transition-colors">
                      <Mail size={13} className="shrink-0 text-gray-500" aria-hidden="true" />
                      {office.email}
                    </a>
                  )}
                </p>
                {office.sub && (
                  <div className="mt-6 pt-5 border-t border-white/10" data-testid={`footer-office-sub-${officeKey}`}>
                    <p className="font-display font-black uppercase tracking-tighter text-base sm:text-lg leading-none text-white">
                      {office.sub.company}
                    </p>
                    {office.sub.subtitle && <p className="mt-2 text-xs text-gray-500">{t(`footer3.offS.${office.sub.sk}`, office.sub.subtitle)}</p>}
                    <p className="mt-3 flex items-start gap-2 text-sm text-gray-400 max-w-md">
                      <MapPin size={14} className="shrink-0 mt-0.5 text-gray-500" aria-hidden="true" />
                      {office.sub.address}
                    </p>
                    {office.sub.email && (
                      <p className="mt-3 flex flex-wrap gap-x-6 gap-y-1.5">
                        {office.sub.phone && (
                          <a href={`tel:${office.sub.phone.replace(/\s/g, "")}`} data-testid={`footer-office-sub-phone-${officeKey}`} className="inline-flex items-center gap-2 font-mono-tech text-sm text-gray-300 hover:text-yellow-500 transition-colors">
                            <Phone size={13} className="shrink-0 text-gray-500" aria-hidden="true" />
                            {office.sub.phone}
                          </a>
                        )}
                        <a href={`mailto:${office.sub.email}`} data-testid={`footer-office-sub-email-${officeKey}`} className="inline-flex items-center gap-2 font-mono-tech text-sm text-gray-300 break-all hover:text-yellow-500 transition-colors">
                          <Mail size={13} className="shrink-0 text-gray-500" aria-hidden="true" />
                          {office.sub.email}
                        </a>
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="border-t border-white/10 pr-0 sm:pr-8 py-5 flex flex-wrap items-center justify-between gap-3">
            <p className="font-display font-black uppercase tracking-tight text-base sm:text-lg text-white">{t("footer3.moreInfo")}</p>
            <Link
              to={lp("/contact")}
              data-testid="footer-contact-cta"
              className="shrink-0 border-2 border-yellow-500 text-yellow-500 px-5 py-2 font-mono-tech text-[11px] uppercase tracking-[0.2em] font-bold hover:bg-yellow-500 hover:text-black transition-colors"
            >
              {t("footer3.contactCta")}
            </Link>
          </div>
        </div>
        <div className="text-gray-300 px-0 sm:px-8 py-12 sm:py-20 lg:pr-8 xl:pr-16 border-t lg:border-t-0 border-white/10 grid sm:grid-cols-2 gap-x-8 gap-y-6 lg:flex lg:justify-between lg:gap-12">
          <div className="sm:col-span-2 lg:col-span-1 lg:max-w-sm">
            <Link to={lp("/")} data-testid="footer-logo-link" className="flex items-baseline gap-1 mb-4 w-fit">
              <img src="/vanrock-logo-word-white.png" alt={`${SITE_NAME} logo`} className="h-8 w-auto" />
              <img src="/vanrock-track-tag-dark.png" alt=".TRACK" className="h-3.5 w-auto" />
            </Link>
            <p className="text-[13px] leading-relaxed text-gray-400 max-w-xl">
              <Trans i18nKey="footer.blurb" components={[<span />, <span />]} />
            </p>
            <button type="button" onClick={openGroupSite} data-testid="footer-global-link" className="mt-3 inline-block font-mono-tech text-[11px] uppercase tracking-[0.2em] text-yellow-500 hover:text-white transition-colors text-left">
              {t("footer.visitGlobal")}
            </button>
            <p className="mt-4 font-mono-tech text-[10px] uppercase tracking-[0.25em] text-yellow-500">
              {t("footer.tagline")}
            </p>
          </div>
          <div>
            <h3 className="font-mono-tech text-[11px] uppercase tracking-[0.25em] text-yellow-500 mb-4">{t("footer.catalog")}</h3>
            <ul className="space-y-2 text-[13px]">
              <li><Link to={lp("/brands")} data-testid="footer-all-brands-link" className="hover:text-yellow-500 transition-colors">{t("footer.allBrands")}</Link></li>
              {FOOTER_TYPES.map((slug, i) => (
                <li key={slug}>
                  <Link to={lp(`/parts/${slug}`)} data-testid={`footer-type-${slug}`} className="hover:text-yellow-500 transition-colors">
                    <span className="font-mono-tech text-[10px] text-gray-500 mr-2">{String(i + 1).padStart(2, "0")}</span>
                    {splitTerm(getMeta(slug).name).local}
                  </Link>
                </li>
              ))}
              <li>
                <button type="button" onClick={openFinalDrives} data-testid="footer-type-final-drive" className="flex items-center gap-2 hover:text-yellow-500 transition-colors">
                  <span className="font-mono-tech text-[10px] text-gray-500 mr-1">{String(FOOTER_TYPES.length + 1).padStart(2, "0")}</span>
                  {t("fd.navLabel", "Final Drives")}
                  <span className="font-mono-tech text-[8px] uppercase tracking-[0.15em] text-yellow-500">↗</span>
                </button>
              </li>
            </ul>
          </div>
          <div>
            <h3 className="font-mono-tech text-[11px] uppercase tracking-[0.25em] text-yellow-500 mb-4">{t("footer.company")}</h3>
            <ul className="space-y-2 text-[13px]">
              <li><Link to={lp("/company")} className="hover:text-yellow-500 transition-colors">{t("nav.company")}</Link></li>
              <li><Link to={lp("/search")} className="hover:text-yellow-500 transition-colors">{t("footer.partSearch")}</Link></li>
              <li><Link to={lp("/request-quote")} data-testid="footer-quote-link" className="hover:text-yellow-500 transition-colors">{t("footer2.requestQuote")}</Link></li>
              <li><button type="button" onClick={openGroupSite} data-testid="footer-group-link" className="hover:text-yellow-500 transition-colors">VANROCK Group ↗</button></li>
              <li><Link to={lp("/mission-vision-values")} data-testid="footer-mvv-link" className="hover:text-yellow-500 transition-colors">{t("mvv.navLabel")}</Link></li>
              <li><Link to={lp("/why-vanrock")} data-testid="footer-why-link" className="hover:text-yellow-500 transition-colors">{t("footer2.why")}</Link></li>
              <li><Link to={lp("/sustainability")} data-testid="footer-sustainability-link" className="hover:text-yellow-500 transition-colors">{t("sust.navLabel")}</Link></li>
              <li><Link to={lp("/careers")} data-testid="footer-careers-link" className="hover:text-yellow-500 transition-colors">{t("careers.navLabel")}</Link></li>
              <li><Link to={lp("/newsroom")} data-testid="footer-newsroom-link" className="hover:text-yellow-500 transition-colors">{t("news.navLabel")}</Link></li>
              <li><Link to={lp("/dealers")} data-testid="footer-find-dealer-link" className="hover:text-yellow-500 transition-colors">{t("footer2.findDealer")}</Link></li>
              <li><Link to={lp("/guides")} data-testid="footer-guides-link" className="hover:text-yellow-500 transition-colors">{t("footer2.guides")}</Link></li>
              <li><Link to={lp("/rubber-track-sizes")} data-testid="footer-track-sizes-link" className="hover:text-yellow-500 transition-colors">{t("footer2.sizeChart")}</Link></li>
              <li><Link to={lp("/answers")} data-testid="footer-answers-link" className="hover:text-yellow-500 transition-colors">{t("footer2.qa")}</Link></li>
              <li>
                <Link to={lp("/become-a-dealer")} data-testid="footer-dealer-link" className="text-yellow-500 font-semibold hover:text-white transition-colors">
                  {t("footer.becomeDealer")}
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
    </div>
    <div className="bg-[#0A0A0A] text-gray-300">
    <div className="max-w-none mx-auto px-4 sm:px-10 lg:px-14">
      <div className="pt-5 border-t border-white/10">
        <p className="text-[10px] leading-relaxed text-gray-400 max-w-4xl normal-case tracking-normal" data-testid="footer-trademark-disclaimer">
          {t("footer.tmDisclaimer")}
        </p>
      </div>
      <div className="py-4 pb-20 sm:pb-10 flex flex-wrap items-center gap-x-8 gap-y-2 font-mono-tech text-[10px] uppercase tracking-[0.2em] text-gray-400">
        <span>© {new Date().getFullYear()} {SITE_NAME} — {t("footer.rights")}</span>
        <span className="flex flex-wrap gap-x-5 gap-y-2">
          <Link to={lp("/privacy-policy")} data-testid="footer-privacy-link" className="hover:text-white transition-colors">{t("legal.privacyLink")}</Link>
          <Link to={lp("/cookie-policy")} data-testid="footer-cookies-link" className="hover:text-white transition-colors">{t("legal.cookieLink")}</Link>
          <Link to={lp("/complaints")} data-testid="footer-complaints-link" className="hover:text-white transition-colors">{t("complaints.link")}</Link>
        </span>
      </div>
    </div>
    </div>
  </footer>
  );
};

export const Layout = ({ children }) => (
  <div className="min-h-screen flex flex-col bg-background">
    <Header />
    <main className="flex-1">{children}</main>
    <Footer />
  </div>
);
